import { Address } from "../../../../modules/common/shared/apis/AddressAPI";
import { Person } from "../../../../modules/common/shared/apis/PersonAPI";
import { GuardianType, Student } from "../../../../modules/common/shared/types";
import { ImportInfo } from "./studentImportValidator";

// `dues`: academic year name -> amount still owed (e.g. { "2024-25": 12000 }); the backend creates the term dues
export type StudentImportPayload = Omit<Student, "totalDue"> & { dues: Record<string, number> };

export const mapRowToStudentDTO = (row: any, commonInfo: ImportInfo, dues: Record<string, number>): StudentImportPayload => {
  // Blank cells come back from xlsx as "" (defval: ""); the backend expects null for
  // "not entered", not an empty string, so every field is normalized through this.
  const nullable = (val: any) => {
    if (val === null || val === undefined) return null;
    const str = String(val).trim();
    return str === "" ? null : val;
  };
  const nullableString = (val: any) => {
    if (val === null || val === undefined) return null;
    const str = String(val).trim();
    return str === "" ? null : str;
  };

  const presentAddress: Address = {
    houseNo: nullableString(row["Present - House No *"]),
    streetRoad: nullableString(row["Present - Street Road *"]),
    areaLocality: nullableString(row["Present - Area Locality"]),
    landmark: nullableString(row["Present - LandMark"]),
    city: nullableString(row["Present - City *"]),
    state: nullableString(row["Present - State *"]),
    pincode: nullable(row["Present - Pincode *"]),
  };

  const isPermanentBlank = !row["Permanent - House No *"] || row["Permanent - House No *"].toString().trim() === "";

  const permanentAddress: Address = isPermanentBlank
    ? { ...presentAddress } // clone present
    : {
        houseNo: nullableString(row["Permanent - House No *"]),
        streetRoad: nullableString(row["Permanent - Street Road *"]),
        areaLocality: nullableString(row["Permanent - Area Locality"]),
        landmark: nullableString(row["Permanent - LandMark"]),
        city: nullableString(row["Permanent - City *"]),
        state: nullableString(row["Permanent - State *"]),
        pincode: nullable(row["Permanent - Pincode *"]),
      };

  const guardianRelation = nullableString(row["Guardian - Relation *"]);

  const guardianJson: Person = {
    name: nullableString(row["Guardian - Name *"]),
    externalId: null,
    dob: nullableString(row["Guardian - DOB"]),
    qualification: nullableString(row["Guardian - Qualification"]),
    occupation: nullableString(row["Guardian - Occupation"]),
    monthlyIncome: nullable(row["Guardian - Monthly Income"]),
    mobileNo: nullable(row["Guardian - Mobile *"]),
    email: nullableString(row["Guardian - Email"]),
  };

  let fatherJson: Person, motherJson: Person, emergencyJson: Person;
  if (GuardianType.FATHER === guardianRelation) {
    fatherJson = guardianJson;
  } else {
    fatherJson = {
      name: nullableString(row["Father - Name *"]),
      externalId: null,
      dob: nullableString(row["Father - DOB"]),
      qualification: nullableString(row["Father - Qualification"]),
      occupation: nullableString(row["Father - Occupation"]),
      monthlyIncome: nullable(row["Father - Monthly Income"]),
      mobileNo: nullable(row["Father - Mobile"]),
      email: nullableString(row["Father - Email"]),
    };
  }

  if (guardianRelation === GuardianType.MOTHER) {
    motherJson = guardianJson;
  } else {
    motherJson = {
      name: nullableString(row["Mother - Name *"]),
      externalId: null,
      dob: nullableString(row["Mother - DOB"]),
      qualification: nullableString(row["Mother - Qualification"]),
      occupation: nullableString(row["Mother - Occupation"]),
      monthlyIncome: nullable(row["Mother - Monthly Income"]),
      mobileNo: nullable(row["Mother - Mobile"]),
      email: nullableString(row["Mother - Email"]),
    };
  }

  let emergencyRelation = nullableString(row["Emergency - Relation *"]);
  if ("Guardian" === nullableString(row["Emergency - Relation *"])) {
    emergencyJson = guardianJson;
    emergencyRelation = guardianRelation;
  } else {
    emergencyJson = {
      name: nullableString(row["Emergency - Name *"]),
      externalId: null,
      dob: nullableString(row["Emergency - DOB"]),
      qualification: nullableString(row["Emergency - Qualification"]),
      occupation: nullableString(row["Emergency - Occupation"]),
      monthlyIncome: nullable(row["Emergency - Monthly Income"]),
      mobileNo: nullable(row["Emergency - Mobile *"]),
      email: nullableString(row["Emergency - Email"]),
    };
  }

  const dto: Partial<StudentImportPayload> = {
    name: nullableString(row["Name *"]),
    admissionNo: nullableString(row["AdmissionNo *"]),
    // convert gender string to char
    gender: nullableString(row["Gender *"]?.trim()?.charAt(0)?.toUpperCase()), // M/F/O
    gradeName: commonInfo.grade,
    sectionName: commonInfo.section,
    dob: nullableString(row["DOB(DD-MMM-YYYY) *"]),

    placeOfBirth: nullableString(row["PlaceOfBirth *"]),
    religion: nullableString(row["Religion"]),
    nationality: nullableString(row["Nationality"]),
    motherTongue: nullableString(row["MotherTongue"]),
    bloodGroup: nullableString(row["BloodGroup"]),
    externalId: null,
    dues,

    // ADDRESS MAPPING
    present: presentAddress,

    permanent: permanentAddress,

    guardian: guardianJson,

    father: fatherJson,

    mother: motherJson,

    emergency: emergencyJson,
    active: false,
    guardianContactRelation: guardianRelation,
    emergencyContactRelation: emergencyRelation,
    requestFrom: "ADMIN",
  };

  // Same as the admin student form: guardian/emergency are sent only when they are not the father or mother
  const isParent = (relation?: string | null) => relation === GuardianType.FATHER || relation === GuardianType.MOTHER;
  if (isParent(guardianRelation)) delete dto.guardian;
  if (isParent(emergencyRelation)) delete dto.emergency;
  return dto as StudentImportPayload;
};
